import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"
import * as React from "react"
import { useHaptic } from "@/shared/hooks/use-haptic"
import { cn } from "@/shared/lib/utils"

const buttonVariants = cva(
	"inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-[transform,background-color,border-color,color,box-shadow] duration-150 ease-[var(--ease-out-soft)] active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
	{
		variants: {
			variant: {
				default: "bg-primary text-primary-foreground shadow hover:bg-primary/90",
				destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
				outline:
					"border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
				secondary: "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
				ghost: "hover:bg-accent hover:text-accent-foreground",
				link: "text-primary underline-offset-4 hover:underline",
			},
			size: {
				default: "h-12 px-4 py-2",
				sm: "h-11 rounded-xl px-3 text-sm",
				lg: "h-12 rounded-xl px-8",
				icon: "h-11 w-11",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
)

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean
	isLoading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			className,
			variant,
			size,
			asChild = false,
			isLoading = false,
			disabled,
			onClick,
			children,
			...props
		},
		ref,
	) => {
		const Comp = asChild ? Slot : "button"
		const { triggerHaptic } = useHaptic()

		const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
			triggerHaptic("light")
			onClick?.(e)
		}

		return (
			<Comp
				className={cn(buttonVariants({ variant, size, className }))}
				ref={ref}
				onClick={handleClick}
				disabled={disabled || isLoading}
				aria-busy={isLoading || undefined}
				{...props}
			>
				{isLoading ? (
					<>
						<Loader2 className="animate-spin" />
						{children}
					</>
				) : (
					children
				)}
			</Comp>
		)
	},
)
Button.displayName = "Button"

export { Button, buttonVariants }
